import { membershipNameType } from '../../../../utils/constant'

export type ChurnReason = 'price' | 'time' | 'usage' | 'goal' | 'broken'

export type SwitchTarget = 'standard_monthly' | 'standard_yearly' | 'weekly_yearly'

export type ChurnOffer =
  | { kind: 'discount'; percentOff: number; durationMonths: number | null } // durationMonths null = applies to the next renewal
  | { kind: 'switch'; target: SwitchTarget }
  | { kind: 'pause' }
  | { kind: 'support' }
  | { kind: 'none' }

export interface ChurnContext {
  membershipName?: string
  /** Both spellings occur in production: the profile normalises on some paths, not all. */
  interval?: 'monthly' | 'yearly' | 'month' | 'year' | null
  /** The customer may claim a retention discount once, ever (server-enforced too). */
  saveOfferUsed: boolean
  /** Currency of the live subscription. The Weekly price is USD-only, so the Weekly
   *  switch is only offered to USD subscribers. */
  currency?: string
  /** False for IAP-managed or lapsed accounts — nothing in Stripe to discount or pause. */
  hasStripeSubscription: boolean
  /** A repeating discount (the 50%-off-3-months save offer) is still running on the
   *  subscription. It suppresses the yearly switches below. */
  repeatingDiscountActive?: boolean
}

/** Switch targets billed yearly. Stripe applies a repeating discount to whatever invoice falls
 *  inside its window, so moving onto one of these while the 50%-off-3-months coupon runs takes
 *  half off a full year rather than three months. The server refuses it too. */
const YEARLY_SWITCH_TARGETS: SwitchTarget[] = ['standard_yearly', 'weekly_yearly']

/**
 * The save-offer matrix. One offer per reason × plan × interval; the server independently
 * validates everything, so this only decides what to present.
 */
export const resolveChurnOffer = (ctx: ChurnContext, reason: ChurnReason): ChurnOffer => {
  const offer = pickChurnOffer(ctx, reason)
  // Applied here rather than inside each branch so a yearly target added later cannot miss it.
  // Offering nothing is the honest answer: this customer is already mid-discount, and every
  // remaining save offer for their plan is billed yearly.
  if (ctx.repeatingDiscountActive && offer.kind === 'switch' && YEARLY_SWITCH_TARGETS.includes(offer.target)) {
    return { kind: 'none' }
  }
  return offer
}

const pickChurnOffer = (ctx: ChurnContext, reason: ChurnReason): ChurnOffer => {
  if (reason === 'broken') return { kind: 'support' }
  if (reason === 'goal') return { kind: 'none' }
  if (!ctx.hasStripeSubscription) return { kind: 'none' }

  const isPro = ctx.membershipName === membershipNameType.Pro
  const isStandard = ctx.membershipName === membershipNameType.Standard
  const isWeekly = ctx.membershipName === membershipNameType.Weekly
  // The profile normalises Stripe's 'month'/'year' to 'monthly'/'yearly' on its main path,
  // but not on every one (the plan-detail and store-order paths pass the raw value straight
  // through), so both spellings reach this function in production. Matching only the long
  // form drops a raw-spelling subscriber past every plan branch below and out of the final
  // `return { kind: 'none' }` — and 'none' skips the offer step entirely, so they are taken
  // straight to the cancel confirmation with nothing offered and no churn_offer_shown event.
  // Between 1 Jul and 8 Sep 2026, 8 of the 42 people in the cancel flow carried the raw
  // spelling; most picked reasons that don't branch on interval ('time' pauses, 'broken'
  // routes to support), so the confirmed loss is small — 1 of the 2 who cited price saw no
  // offer. Small, but it is silent and it costs the whole save attempt.
  // PlanTab already accepts both spellings for the same reason.
  const monthly = ctx.interval === 'monthly' || ctx.interval === 'month'
  const yearly = ctx.interval === 'yearly' || ctx.interval === 'year'
  const usd = (ctx.currency || 'USD').toUpperCase() === 'USD'

  if (reason === 'time') return { kind: 'pause' }

  if (reason === 'price') {
    if (isPro && monthly) {
      return ctx.saveOfferUsed
        ? { kind: 'switch', target: 'standard_monthly' }
        : { kind: 'discount', percentOff: 50, durationMonths: 3 }
    }
    if (isPro && yearly) {
      return ctx.saveOfferUsed
        ? { kind: 'switch', target: 'standard_yearly' }
        : { kind: 'discount', percentOff: 30, durationMonths: null }
    }
    if (isStandard && monthly) {
      // Standard monthly is the largest group of leavers and "price" is their commonest
      // reason. They used to be shown the yearly switch on the theory that a third less
      // per month is the strongest offer at zero margin cost — but it answers "this costs
      // too much" with "pay twelve months up front", and between 1 Jul and 8 Sep 2026 the
      // whole save flow converted 1 of 37 offers. Pro monthly already gets a real discount
      // for the same reason; this gives Standard monthly the same one, and keeps the yearly
      // switch as the second ask once the once-ever discount is spent.
      return ctx.saveOfferUsed
        ? { kind: 'switch', target: 'standard_yearly' }
        : { kind: 'discount', percentOff: 50, durationMonths: 3 }
    }
    if (isStandard && yearly) {
      if (!ctx.saveOfferUsed) return { kind: 'discount', percentOff: 30, durationMonths: null }
      return usd ? { kind: 'switch', target: 'weekly_yearly' } : { kind: 'none' }
    }
    if (isWeekly) {
      return ctx.saveOfferUsed ? { kind: 'none' } : { kind: 'discount', percentOff: 40, durationMonths: null }
    }
    return { kind: 'none' }
  }

  // reason === 'usage' — preserve the habit on a lighter tier.
  if (isPro) {
    return { kind: 'switch', target: monthly ? 'standard_monthly' : 'standard_yearly' }
  }
  if (isStandard) {
    if (usd) return { kind: 'switch', target: 'weekly_yearly' }
    return monthly ? { kind: 'switch', target: 'standard_yearly' } : { kind: 'none' }
  }
  return { kind: 'none' }
}
